/* Featured Games — chips rail + modal video control
   - Mutes chips by default; optional hover-to-unmute for HTML5 video chips
   - Real <dialog> with focus trap; ESC & close button supported
   - On open: play + unmute modal video (HTML5), enable YT/Vimeo JS API & play via postMessage
   - On close: pause + remute everything
*/
(() => {
  if (window.__FGV_BOOTED__) return; window.__FGV_BOOTED__ = true;

  const qs = (s, r=document) => r.querySelector(s);
  const qsa = (s, r=document) => Array.from(r.querySelectorAll(s));

  // ----- Dialog helpers -----
  function trapFocus(dialog) {
    const FOCUSABLE = [
      'a[href]','area[href]','button:not([disabled])','input:not([disabled])',
      'select:not([disabled])','textarea:not([disabled])','details summary',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');
    function onKey(e){
      if (e.key !== 'Tab') return;
      const focusables = qsa(FOCUSABLE, dialog).filter(el => el.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0], last = focusables[focusables.length-1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    dialog.__fgvOnKey = onKey;
    dialog.addEventListener('keydown', onKey);
  }
  function untrapFocus(dialog) {
    if (dialog.__fgvOnKey) dialog.removeEventListener('keydown', dialog.__fgvOnKey);
    dialog.__fgvOnKey = null;
  }

  // ----- Media control -----
  function safePlay(v){ try { const p = v.play?.(); if (p && typeof p.catch === 'function') p.catch(()=>{}); } catch(_){} }
  function pause(v){ try { v.pause?.(); } catch(_){} }
  function remute(v){ try { v.muted = true; v.volume = 0; } catch(_){} }

  function ensureYTEnableJSAPI(iframe){
    try{
      const u = new URL(iframe.src, location.href);
      if (!/youtube\.com|youtu\.be/.test(u.hostname)) return;
      if (u.hostname.includes('youtu.be')) return; // external_video_tag uses youtube.com embed, youtu.be unlikely here
      if (u.searchParams.get('enablejsapi') !== '1') {
        u.searchParams.set('enablejsapi','1');
        iframe.src = u.toString();
      }
    }catch{}
  }

  function playIframe(iframe){
    try{
      const u = new URL(iframe.src, location.href);
      if (/youtube\.com/.test(u.hostname)) {
        ensureYTEnableJSAPI(iframe);
        iframe.contentWindow?.postMessage(JSON.stringify({
          event:'command', func:'playVideo', args:[]
        }), '*');
        // also try unmute
        iframe.contentWindow?.postMessage(JSON.stringify({
          event:'command', func:'unMute', args:[]
        }), '*');
        return;
      }
      if (/vimeo\.com/.test(u.hostname)) {
        iframe.contentWindow?.postMessage({ method:'play' }, '*');
        iframe.contentWindow?.postMessage({ method:'setVolume', value:1 }, '*');
      }
    }catch{}
  }
  function pauseIframe(iframe){
    try{
      const u = new URL(iframe.src, location.href);
      if (/youtube\.com/.test(u.hostname)) {
        iframe.contentWindow?.postMessage(JSON.stringify({
          event:'command', func:'pauseVideo', args:[]
        }), '*');
        return;
      }
      if (/vimeo\.com/.test(u.hostname)) {
        iframe.contentWindow?.postMessage({ method:'pause' }, '*');
      }
    }catch{}
  }

  function closeDialog(dialog){
    // Pause & remute any media inside
    qsa('.fgv-video--modal', dialog).forEach(v => { pause(v); remute(v); });
    qsa('.fgv-iframe', dialog).forEach(pauseIframe);
    try { dialog.close(); } catch(_){}
    untrapFocus(dialog);
    // Return focus to opener
    const openerSel = dialog.getAttribute('data-opener');
    const opener = openerSel ? qs(openerSel) : null;
    opener?.focus?.();
  }

  function openDialog(dialog, openerSel){
    if (openerSel) dialog.setAttribute('data-opener', openerSel);
    try { dialog.showModal(); } catch(_) { dialog.setAttribute('open','open'); }
    trapFocus(dialog);

    // Auto-play modal media
    const video = qs('.fgv-video--modal', dialog);
    const iframe = qs('.fgv-iframe', dialog);
    if (video) { try { video.muted = false; video.volume = 1; } catch(_){} safePlay(video); }
    if (iframe) { playIframe(iframe); }

    // Focus close button
    qs('[data-fgv-close]', dialog)?.focus?.();
  }

  // ----- Chips (rail) behavior -----
  function bootSection(section){
    const hoverUnmute = section.dataset.hoverUnmute === 'true';
    // Hover-to-unmute only applies to HTML5 chip videos
    if (hoverUnmute){
      qsa('.fgv-video--chip', section).forEach(v => {
        // force muted autoplay safe defaults
        try { v.muted = true; v.volume = 0; v.playsInline = true; v.setAttribute('playsinline',''); } catch(_){}
        safePlay(v);
        v.addEventListener('mouseenter', () => { try { v.muted = false; v.volume = 1; } catch(_){}} );
        v.addEventListener('mouseleave', () => { remute(v); });
        // also remute when chip loses visibility (cheap observer)
        document.addEventListener('visibilitychange', () => { if (document.hidden) remute(v); });
      });
    } else {
      qsa('.fgv-video--chip', section).forEach(v => { try { v.muted = true; v.volume = 0; } catch(_){} safePlay(v); });
    }

    // Wire open buttons -> dialogs
    qsa('[data-fgv-open]', section).forEach(btn => {
      const sel = btn.getAttribute('data-fgv-open');
      btn.addEventListener('click', () => {
        const dialog = sel ? qs(sel) : null;
        if (!dialog) return;
        openDialog(dialog, `[data-fgv-open="${sel}"]`);
      });
    });

    // Close buttons
    qsa('dialog[data-fgv-modal]', section).forEach(dialog => {
      qs('[data-fgv-close]', dialog)?.addEventListener('click', () => closeDialog(dialog));
      dialog.addEventListener('cancel', (e) => { e.preventDefault(); closeDialog(dialog); }); // ESC
      // Click outside to close (backdrop click)
      dialog.addEventListener('click', (e) => {
        const rect = dialog.querySelector('.fgv-modal__content')?.getBoundingClientRect();
        if (!rect) return;
        const inBox = e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
        if (!inBox) closeDialog(dialog);
      });

      // Ensure embeds have APIs enabled when present
      qsa('iframe.fgv-iframe', dialog).forEach(ensureYTEnableJSAPI);
    });
  }

  function bootAll(){
    qsa('section.fgv-wrap').forEach(bootSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }

  // Support Theme Editor dynamic reloads
  document.addEventListener('shopify:section:load', (e) => {
    const wrap = e.target?.querySelector?.('section.fgv-wrap');
    if (wrap) bootSection(wrap);
  });
})();
